// frontend/src/hooks/useSalesData.js
// Hooks for sales reporting data.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { useAuth } from '../contexts/AuthContext';
import { kpi as kpiKey, timelines as timelinesKey } from '../queries/keys';
import { useTimeSeriesFetcher } from './useTimeSeriesFetcher';
import { scopeParams } from '../features/item-filter/scopeParams';
import { readResult } from '../queries/api';
import { useTabPollInterval } from '../tabs/tabsContexts';

const THIRTY_SEC = 30 * 1000;
const SIXTY_SEC = 60 * 1000;

/**
 * Fetch sales KPI data: MTD value, change since last month, daily cumulative.
 * Used by the KPI strip Sales tile.
 *
 * Caching matches the locked-in defaults: 30s staleTime + 60s polling
 * while the tab is on screen. See useInventoryKpi for the rationale.
 */
export function useSalesKpi() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const refetchInterval = useTabPollInterval(SIXTY_SEC);
  const query = useQuery({
    queryKey: kpiKey.sales(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/reports/sales/kpi/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    refetchInterval,
    enabled: !!storeId,
  });
  return readResult(query, t);
}

/**
 * Fetch sales progress data for stacked bar chart.
 * Re-fetches when any param changes.
 */
export function useSalesProgress({ metric, granularity, months = 4, groupBy = 'group', scope }) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const params = {
    metric,
    granularity,
    months: String(months),
    group_by: groupBy,
    ...scopeParams(scope),
  };

  return useTimeSeriesFetcher({
    queryKey: timelinesKey.salesProgress(storeId, params),
    endpoint: '/api/reports/sales/progress/',
    params,
    enabled: !!storeId,
  });
}
